import {
  ChangeEvent,
  FC,
  KeyboardEvent,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';

import {
  Combobox,
  ComboboxDropdown,
  ComboboxOption,
  ComboboxSelectButton,
  ComboboxSelectCheck,
  Icon,
  Input,
  Stack,
} from '@eluve/components';
import { useTranslation } from '@eluve/i18n';
import { MEDICAL_SPECIALTIES, isKnownSpecialty } from '@eluve/utils';

export interface SpecialtySelectorComboBoxProps {
  selectedSpecialty: string | null;
  onSelectedSpecialty: (specialty: string | null) => void;
  disabled?: boolean;
  placeholder?: string;
  variant?: 'default' | 'subtle' | 'solid';
}

export const SpecialtySelectorComboBox: FC<SpecialtySelectorComboBoxProps> = ({
  selectedSpecialty,
  onSelectedSpecialty,
  disabled = false,
  placeholder,
  variant,
}) => {
  const { t } = useTranslation();
  const resolvedPlaceholder =
    placeholder ?? t('blocks.specialty.defaultPlaceholder');
  const [otherSpecialty, setOtherSpecialty] = useState<string | null>(null);
  const [isEditing, setIsEditing] = useState(false);
  const inputRef = useRef<HTMLInputElement | null>(null);

  const isOtherSpecialty =
    selectedSpecialty != null && !isKnownSpecialty(selectedSpecialty);

  useEffect(() => {
    if (isEditing && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isEditing]);

  const handleSpecialtyChange = useCallback(
    (specialty: string | null) => {
      if (specialty === 'Other') {
        setIsEditing(true);
        setOtherSpecialty('');
        return;
      }
      setIsEditing(false);
      onSelectedSpecialty(specialty);
    },
    [onSelectedSpecialty],
  );

  const handleOtherSpecialtyBlur = useCallback(() => {
    const specialty = (otherSpecialty ?? '').trim();
    if (specialty && specialty !== selectedSpecialty) {
      onSelectedSpecialty(specialty);
      setIsEditing(false);
    } else if (specialty) {
      // User has entered something but it matches existing - just exit edit mode
      setIsEditing(false);
    }
    // If empty, keep isEditing true so input stays visible
  }, [otherSpecialty, selectedSpecialty, onSelectedSpecialty]);

  const handleOtherSpecialtyChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      setOtherSpecialty(event.target.value);
    },
    [],
  );

  const handleOtherSpecialtyKeyDown = useCallback(
    (event: KeyboardEvent<HTMLInputElement>) => {
      if (['Enter', 'Escape'].includes(event.key)) {
        inputRef.current?.blur();
      }
    },
    [],
  );

  const displayValue =
    isEditing || isOtherSpecialty
      ? t('blocks.specialty.other')
      : (selectedSpecialty ?? resolvedPlaceholder);

  return (
    <Stack gap={2} align="stretch">
      <Combobox disabled={disabled}>
        <ComboboxSelectButton
          className="w-full justify-between"
          variant={variant}
          dropdownIcon={
            <Icon name="ChevronDown" className="ml-2 shrink-0 opacity-50" />
          }
        >
          {displayValue}
        </ComboboxSelectButton>
        <ComboboxDropdown
          searchPlaceholder={t('blocks.specialty.searchPlaceholder')}
          className="max-h-60 min-w-52"
        >
          {selectedSpecialty !== null && (
            <ComboboxOption onSelect={() => handleSpecialtyChange(null)}>
              <Icon className="mr-2" name="X" />
              {t('blocks.specialty.clearSpecialty')}
            </ComboboxOption>
          )}
          {[...MEDICAL_SPECIALTIES, 'Other'].map((specialty) => (
            <ComboboxOption
              key={specialty}
              onSelect={() => handleSpecialtyChange(specialty)}
            >
              <ComboboxSelectCheck
                className="mr-2"
                selected={
                  isEditing || isOtherSpecialty
                    ? specialty === 'Other'
                    : specialty === selectedSpecialty
                }
              />
              {specialty === 'Other' ? t('blocks.specialty.other') : specialty}
            </ComboboxOption>
          ))}
        </ComboboxDropdown>
      </Combobox>
      {(isEditing || isOtherSpecialty) && (
        <Input
          disabled={disabled}
          onBlur={handleOtherSpecialtyBlur}
          onChange={handleOtherSpecialtyChange}
          onFocus={() => setIsEditing(true)}
          onKeyDown={handleOtherSpecialtyKeyDown}
          placeholder={t('blocks.specialty.enterSpecialty')}
          ref={inputRef}
          value={
            isEditing
              ? (otherSpecialty ?? selectedSpecialty ?? '')
              : (selectedSpecialty ?? '')
          }
        />
      )}
    </Stack>
  );
};
